import { useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { sectionVerticesAtom, sectionWellsAtom } from './atoms';
import type { SectionVertex } from './types';

/**
 * Holds a rapidly-changing value still for a moment, so live edits coalesce into one
 * refetch. The same shape the market-data page uses for its chart query.
 *
 * @param {T} value - The value to settle.
 * @param {number} delayMs - How long it has to hold still.
 * @return {T} The settled value.
 */
export const useSettledValue = <T>(value: T, delayMs: number): T => {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return settled;
};

/**
 * The line a section has to ask questions about: its picked wells in Well to Well, its
 * drawn vertices in Arbitrary Line.
 *
 * In pick order, which is the line before the backend has ordered the traverse. The
 * settings panel measures its nearby sweep from the ordered line instead, because that
 * is the line drawn on the map; this one answers a coarser question — which collections
 * does the section go anywhere near — and reordering the same wells does not change the
 * corridor enough to matter to it.
 *
 * A well the picker just added has no coordinates until the cut places it, so it cannot
 * contribute to the line.
 *
 * @return {Array} The line's vertices, in pick order.
 */
export const useSectionLineVertices = (): SectionVertex[] => {
  const wells = useAtomValue(sectionWellsAtom);
  const drawn = useAtomValue(sectionVerticesAtom);

  return useMemo(() => {
    if (wells.length === 0) {
      return drawn;
    }
    return wells.flatMap((well) => {
      if (well.lat === null || well.lon === null) {
        return [];
      }
      return [{ lat: well.lat, lon: well.lon }];
    });
  }, [wells, drawn]);
};
